import type { MessageTab, MessageThreadCounts } from './types'

interface ListTicketsDescriptor {
  tab: MessageTab
  query: string | null
  counts: MessageThreadCounts | null
}

/** `failed` still owns a list: the view fetches for itself, so it can recover. */
export type ListTicketsOutcome =
  | ({ kind: 'descriptor' } & ListTicketsDescriptor)
  | { kind: 'failed' }

const TABS: MessageTab[] = ['all', 'awaiting', 'with_heard', 'resolved']

const isTab = (value: unknown): value is MessageTab =>
  typeof value === 'string' && TABS.some((tab) => tab === value)

const isCounts = (value: unknown): value is MessageThreadCounts =>
  typeof value === 'object' &&
  value !== null &&
  'awaiting' in value &&
  'withHeard' in value &&
  'resolved' in value &&
  typeof value.awaiting === 'number' &&
  typeof value.withHeard === 'number' &&
  typeof value.resolved === 'number'

export const parseListTicketsResult = (
  raw: unknown
): ListTicketsOutcome | null => {
  let value = raw
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value)
    } catch {
      return null
    }
  }

  // The flag-off tool still answers with the legacy array. Refusing it here
  // keeps that path on the card it has always rendered, so turning the flag
  // off is a real rollback.
  if (Array.isArray(value)) return null
  if (typeof value !== 'object' || value === null) return null
  if ('error' in value) return { kind: 'failed' }
  if (!('view' in value) || value.view !== 'messages') return null

  const tab = 'tab' in value && isTab(value.tab) ? value.tab : 'awaiting'
  const query =
    'query' in value && typeof value.query === 'string' ? value.query : null
  const counts =
    'counts' in value && isCounts(value.counts)
      ? {
          awaiting: value.counts.awaiting,
          withHeard: value.counts.withHeard,
          resolved: value.counts.resolved,
        }
      : null

  return { kind: 'descriptor', tab, query, counts }
}
